import PropTypes from 'prop-types'
import { Fragment, useCallback } from 'react'
import {
  Asset,
  Conditional,
  renderIcon,
  kuiperClassNames,
} from '@opallabs/kuiper'
import { AccountName } from '../../../../../AccountName'
import { ContentAvatar } from '../../../../../ContentAvatar'
import {
  sharedColors,
  sharedStyles,
} from '../../../sharedDisplays/sharedStyles'
import { addComment, timestamp } from '../../../sharedDisplays/staticText'
import { TextAreaWithAccountName } from '../../../sharedDisplays/TextAreaWithAccountName'
import { styles } from './styles'

export const DesktopSingle = ({
  accountImage,
  accountName,
  asset,
  collabAccount,
  content = '',
  instagramCollab,
  isPresenting,
  location,
  maintainAspectRatio = false,
  onContentChange,
  onExpandAsset,
  onRemoveAsset,
  readOnly,
  updateCaption,
}) => {
  const renderResponseIcon = (glyph) =>
    renderIcon(glyph, { height: 24, width: 24, ariaMode: 'hidden' })

  const getSecondAccountProps = (collabAccount) => ({
    name: collabAccount || 'No collab account',
    color: collabAccount ? 'inherit' : sharedColors.emptyCollab,
    style: collabAccount ? styles.accountNameStyles : styles.emptyCollabStyles,
  })

  const getImageOrientation = useCallback(() => {
    if (!asset) return null
    if (asset.height >= asset.width) return 'portrait'
    return 'landscape'
  }, [asset])

  const imageOrientation = getImageOrientation()

  return (
    <div css={styles.flexRow}>
      <Asset
        imageFit="cover"
        hasAssetOptions={true}
        hasAssetOverlay={Boolean(asset) && (!readOnly || isPresenting)}
        assetReference={asset}
        placeholder={true}
        removeable={!isPresenting}
        onEmbiggen={() => onExpandAsset(asset.id)}
        onRemove={() => onRemoveAsset(asset.id)}
        updateCaption={updateCaption}
        overrideStyles={{
          asset: styles.makeAssetStyles(maintainAspectRatio, imageOrientation),
          assetBase: styles.makeAssetBaseStyles(
            maintainAspectRatio,
            imageOrientation
          ),
        }}
      />
      <div
        css={styles.postInfoSection}
        className={kuiperClassNames('instagram-post-info')}
      >
        <div
          css={styles.header}
          className={kuiperClassNames('instagram-post-header')}
        >
          <div
            css={styles.avatarUsername}
            className={kuiperClassNames('instagram-avatar-username')}
          >
            {instagramCollab ? (
              <Fragment>
                <div css={sharedStyles.collabAvatarStyles}>
                  <ContentAvatar assetReference={accountImage} size={24} />
                  <ContentAvatar
                    assetReference={null}
                    size={24}
                    style={sharedStyles.secondAvatarStyles}
                  />
                </div>
                <div css={styles.accountNameWrapperStyles}>
                  <AccountName
                    name={accountName}
                    style={styles.accountNameStyles}
                  />
                  <span> and </span>
                  <AccountName {...getSecondAccountProps(collabAccount)} />
                </div>
              </Fragment>
            ) : (
              <Fragment>
                <ContentAvatar
                  assetReference={accountImage}
                  style={styles.avatar}
                />
                <AccountName
                  color={sharedColors.darkGreyText}
                  name={accountName}
                  size={15}
                  style={styles.account(location, 'desktopSingle')}
                />
              </Fragment>
            )}
            <Conditional condition={location}>
              <span
                css={styles.location}
                className={kuiperClassNames('instagram-location')}
              >
                {location}
              </span>
            </Conditional>
          </div>
          {renderIcon('instagram-desktop-more', { ariaMode: 'hidden' })}
        </div>

        <hr css={styles.divider} />

        <div
          css={styles.contentWrapper}
          className={kuiperClassNames('instagram--textarea-wrapper')}
        >
          <div
            css={styles.textareaContentWrapper}
            className={kuiperClassNames('instagram--textarea-content-wrapper')}
          >
            <ContentAvatar
              assetReference={accountImage}
              style={styles.textareaAvater}
            />
          </div>
          <TextAreaWithAccountName
            content={content}
            readOnly={readOnly}
            isPresenting={isPresenting}
            display="desktopSingle"
            contentCharacterLimit={2200}
            characterCounterOptions={{ position: 'left', offset: 48 }}
            onContentChange={onContentChange}
            style={styles.textareaWrapper(
              accountName && accountName.length >= 30
            )}
            textareaStyle={() =>
              styles.textarea(accountName && accountName.length >= 30)
            }
            accountMeasurerStyle={styles.accountMeasurer}
            truncationLimit={125}
            username={accountName}
          />
        </div>
        <ul css={styles.responseList}>
          <li css={styles.responseItem({ bookmark: false })}>
            {renderResponseIcon('instagram-mobile-heart')}
          </li>
          <li css={styles.responseItem({ bookmark: false })}>
            {renderResponseIcon('instagram-mobile-comment')}
          </li>
          <li css={styles.responseItem({ bookmark: false })}>
            {renderResponseIcon('instagram-mobile-send')}
          </li>
          <li css={styles.responseItem({ bookmark: true })}>
            {renderResponseIcon('instagram-mobile-bookmark')}
          </li>
        </ul>
        <div
          css={styles.postedAt}
          className={kuiperClassNames('instagram-posted-at')}
        >
          {timestamp}
        </div>
        <div
          css={styles.addCommentText}
          className={kuiperClassNames('instagram-add-comment')}
        >
          {addComment}
        </div>
      </div>
    </div>
  )
}

DesktopSingle.propTypes = {
  accountImage: PropTypes.object,
  accountName: PropTypes.string,
  asset: PropTypes.object,
  collabAccount: PropTypes.string,
  content: PropTypes.string,
  instagramCollab: PropTypes.bool,
  isPresenting: PropTypes.bool,
  location: PropTypes.string,
  maintainAspectRatio: PropTypes.bool,
  onContentChange: PropTypes.func,
  onExpandAsset: PropTypes.func,
  onRemoveAsset: PropTypes.func,
  readOnly: PropTypes.bool,
  updateCaption: PropTypes.func,
}
